import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../pages/PageLayout';
import '../styles/LegalPages.css';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <div className="legal-page-container">
        <div className="legal-content">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-updated">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section className="legal-section">
            <h2 className="section-title">1. Acceptance of Terms</h2>
            <p>
              Welcome to AllSportsHubs. By accessing or using our sports scheduling platform ("Service"), 
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
              you may not access or use the Service.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and AllSportsHubs. We reserve 
              the right to update these Terms at any time. Your continued use of the Service after changes 
              are posted constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">2. Description of Service</h2>
            <p>
              AllSportsHubs provides an online platform for sports league management, including:
            </p>
            <ul className="legal-list">
              <li>Automated schedule generation using round-robin algorithms</li>
              <li>Venue management and assignment capabilities</li>
              <li>Team organization and management tools</li>
              <li>Schedule editing and customization features</li>
              <li>Data export and sharing functionality</li>
              <li>Account management and user authentication</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time 
              with or without notice.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">3. User Accounts</h2>
            
            <h3 className="subsection-title">3.1 Account Creation</h3>
            <p>
              To use certain features of our Service, you must create an account. When creating an account, 
              you agree to:
            </p>
            <ul className="legal-list">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3 className="subsection-title">3.2 Account Eligibility</h3>
            <p>
              You must be at least 13 years old to create an account. By creating an account, you represent 
              that you meet this age requirement. If you are under 18, you confirm that you have obtained 
              parental or guardian consent to use the Service.
            </p>

            <h3 className="subsection-title">3.3 Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate your account at any time for violation of these 
              Terms, fraudulent activity, or any other reason we deem appropriate. You may delete your account 
              at any time through your account settings.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">4. User Conduct and Responsibilities</h2>
            <p>When using our Service, you agree not to:</p>
            <ul className="legal-list">
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use automated systems (bots, scrapers) without permission</li>
              <li>Transmit any viruses, malware, or harmful code</li>
              <li>Impersonate any person or entity</li>
              <li>Collect or store personal data about other users</li>
              <li>Use the Service to spam, harass, or harm others</li>
              <li>Reproduce, duplicate, or copy any part of the Service without permission</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">5. User Content</h2>
            
            <h3 className="subsection-title">5.1 Your Content</h3>
            <p>
              You retain all rights to content you create using our Service, including schedules, venue 
              information, and team data ("User Content"). By using the Service, you grant us a non-exclusive, 
              worldwide, royalty-free license to host, store, and display your User Content as necessary to 
              provide the Service.
            </p>

            <h3 className="subsection-title">5.2 Content Responsibility</h3>
            <p>
              You are solely responsible for your User Content and the consequences of posting or publishing it. 
              You represent and warrant that:
            </p>
            <ul className="legal-list">
              <li>You own or have the necessary rights to your User Content</li>
              <li>Your User Content does not violate any third-party rights</li>
              <li>Your User Content complies with these Terms and applicable laws</li>
            </ul>

            <h3 className="subsection-title">5.3 Content Removal</h3>
            <p>
              We reserve the right to remove or modify any User Content that violates these Terms or that 
              we determine to be harmful, offensive, or inappropriate, at our sole discretion.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">6. Intellectual Property Rights</h2>
            <p>
              The Service and all content, features, and functionality (excluding User Content) are owned 
              by AllSportsHubs and are protected by copyright, trademark, and other intellectual property laws. 
              You may not:
            </p>
            <ul className="legal-list">
              <li>Copy, modify, or create derivative works of the Service</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Remove or alter any copyright, trademark, or proprietary notices</li>
              <li>Use our trademarks or branding without written permission</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">7. Privacy and Data Protection</h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy, which explains how we collect, 
              use, and protect your personal information. By using the Service, you consent to our data 
              practices as described in the Privacy Policy.
            </p>
            <p>
              We implement reasonable security measures to protect your data, but we cannot guarantee absolute 
              security. You acknowledge that you provide information at your own risk.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">8. Third-Party Services and Links</h2>
            <p>
              Our Service may contain links to third-party websites or integrate with third-party services 
              (such as Google OAuth and Supabase). We are not responsible for the content, privacy policies, 
              or practices of third-party services. Your use of third-party services is at your own risk and 
              subject to their terms and policies.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">9. Disclaimers and Limitations of Liability</h2>
            
            <h3 className="subsection-title">9.1 Service "As Is"</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3 className="subsection-title">9.2 No Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALLSPORTSHUBS SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, 
              WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER 
              INTANGIBLE LOSSES.
            </p>

            <h3 className="subsection-title">9.3 Data Loss</h3>
            <p>
              While we implement backup systems, we are not responsible for any loss of data or content. 
              You are responsible for maintaining your own backups of important data.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless AllSportsHubs and its officers, directors, 
              employees, and agents from any claims, liabilities, damages, losses, and expenses, including 
              reasonable attorney's fees, arising out of or in any way connected with:
            </p>
            <ul className="legal-list">
              <li>Your access to or use of the Service</li>
              <li>Your User Content</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">11. Dispute Resolution</h2>
            <p>
              Any disputes arising from these Terms or your use of the Service shall be resolved through 
              binding arbitration in accordance with the rules of the American Arbitration Association, 
              except where prohibited by law. You agree to waive your right to a jury trial or to participate 
              in a class action lawsuit.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions. You agree to submit to the personal jurisdiction 
              of the courts located within the United States for the resolution of any disputes.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">13. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall 
              be limited or eliminated to the minimum extent necessary, and the remaining provisions shall 
              remain in full force and effect.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">14. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and 
              AllSportsHubs regarding the use of the Service and supersede all prior agreements and 
              understandings.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">15. Contact Information</h2>
            <p>
              If you have questions about these Terms, please contact us through your account settings or 
              visit our support page. We will make reasonable efforts to respond to your inquiries in a 
              timely manner.
            </p>
          </section>

          <div className="legal-notice">
            <p>
              <strong>Note:</strong> By creating an account or using our Service, you acknowledge that you 
              have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>

          <div className="legal-footer">
            <button
              className="legal-back-btn"
              onClick={() => navigate(-1)}
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TermsOfService;